import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Card,
  Spinner,
  Alert,
  Tabs,
  Tab,
  Button,
} from "react-bootstrap";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { toast } from "react-hot-toast";
import { OverlayTrigger, Tooltip as BSTooltip } from "react-bootstrap";
import axiosInstance from "../axiosInstance";
import { getEnv } from "../environment";

const env = getEnv();
const api_url = env.url;

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

// TEI (Text Embeddings Inference) models — no TGI-specific metrics tracked
const TEI_MODELS = [
  "llmaas-bge-large-en-15",
  "llmaas-bge-base-en-15",
  "llmaas-gte-large-en-15",
  "llmaas-nomic-embed-text-15",
];

const isTeiModel = (model) => {
  const name = model?.toLowerCase().replace(/-dev$/, "") ?? "";
  return TEI_MODELS.some((t) => name === t.toLowerCase());
};

// Wrapper for ResponsiveContainer to suppress dimension warnings
const SafeResponsiveContainer = ({ children, height = 300, ...props }) => (
  <ResponsiveContainer width="99%" height={height} debounceDelay={100}>
    {children}
  </ResponsiveContainer>
);

// Helper to get last 7 days as default date range
const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 7);
  return {
    start: start.toISOString().split("T")[0],
    end: end.toISOString().split("T")[0],
  };
};

// Specialized converters
const toUserArray = (data) =>
  Array.isArray(data)
    ? data
    : Object.entries(data || {}).map(([user, count]) => ({ user, count }));

const toNameValueArray = (data) =>
  Array.isArray(data)
    ? data
    : Object.entries(data || {}).map(([name, value]) => ({ name, value }));

// Normalizes date->count maps or arrays into an array with {date, count}
const toDateCountArray = (data) => {
  if (Array.isArray(data)) {
    return data.map((d) => ({
      date: d?.date || d?.name || d?.key || "",
      count: d?.count ?? d?.value ?? 0,
    }));
  }
  return Object.entries(data || {}).map(([date, value]) => ({
    date,
    count:
      typeof value === "number" ? value : (value?.count ?? value?.value ?? 0),
  }));
};

// Normalizes model performance maps into an array with {model, avgResponseTime, avgTokens}
const toModelPerfArray = (data) => {
  if (Array.isArray(data)) {
    return data.map((d) => ({
      model: d?.model || d?.name || "",
      avgResponseTime:
        // Convert seconds to ms if using avgTotalTime/avgInferenceTime
        (d?.avgTotalTime ?? d?.avgInferenceTime) != null
          ? Math.round((d?.avgTotalTime ?? d?.avgInferenceTime) * 1000)
          : (d?.avgResponseTime ??
            d?.avgLatency ??
            d?.latency ??
            d?.value ??
            0),
      avgTokens:
        d?.avgTokens ??
        d?.avgTokensGenerated ??
        d?.averageTokens ??
        d?.averageTokensGenerated ??
        d?.tokens ??
        d?.totalTokens ??
        0,
    }));
  }
  return Object.entries(data || {}).map(([model, stats]) => ({
    model,
    avgResponseTime:
      // Convert seconds to ms if using avgTotalTime/avgInferenceTime
      (stats?.avgTotalTime ?? stats?.avgInferenceTime) != null
        ? Math.round((stats?.avgTotalTime ?? stats?.avgInferenceTime) * 1000)
        : (stats?.avgResponseTime ??
          stats?.avgLatency ??
          stats?.latency ??
          stats?.value ??
          0),
    avgTokens:
      stats?.avgTokens ??
      stats?.avgTokensGenerated ??
      stats?.averageTokens ??
      stats?.averageTokensGenerated ??
      stats?.tokens ??
      stats?.totalTokens ??
      0,
  }));
};

// Formats date strings for month-only X axis ticks
// const formatMonthTick = (value) => {
//   if (!value) return "";
//   const d = new Date(value);
//   if (Number.isNaN(d.getTime())) return value;
//   return d.toLocaleString("en-US", { month: "short" });
// };

// Calculate dynamic Y-axis width based on the longest label
const calculateYAxisWidth = (
  data,
  key = "user",
  minWidth = 80,
  maxWidth = 300,
) => {
  if (!data || !Array.isArray(data) || data.length === 0) return minWidth;
  const longestLabel = data.reduce((max, item) => {
    const label = String(item?.[key] || "");
    return label.length > max.length ? label : max;
  }, "");
  // Approximate 7px per character + some padding
  const calculatedWidth = Math.min(
    maxWidth,
    Math.max(minWidth, longestLabel.length * 7 + 20),
  );
  return calculatedWidth;
};

const formatProvisionTypeLabel = (type) => {
  if (!type) return "";
  const normalized = String(type).toLowerCase();
  if (
    normalized === "gm" ||
    normalized === "galileo" ||
    normalized === "gm-provisioned"
  ) {
    return "GM Provisioned";
  }
  if (normalized === "self" || normalized === "self-provisioned") {
    return "Self Provisioned";
  }
  return type;
};

const MetricsVisual = () => {
  const defaultDates = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [provisionType, setProvisionType] = useState("all");
  const [selectedModel, setSelectedModel] = useState("all");
  const [loading, setLoading] = useState(false);
  const [aggregatedData, setAggregatedData] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableProvisionTypes, setAvailableProvisionTypes] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState("7d");
  const [activeUserTab, setActiveUserTab] = useState("client-id");
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Memoized model performance data for bar chart (backend pre-filters by provisionType)
  const modelPerfData = useMemo(() => {
    const candidate =
      aggregatedData?.modelPerformance ??
      aggregatedData?.modelPerformanceMap ??
      aggregatedData?.modelPerformanceStats;
    return toModelPerfArray(candidate);
  }, [aggregatedData]);

  // Memoized requests by model for pie chart (backend pre-filters by provisionType)
  const tgiRequestsByModelData = useMemo(() => {
    return toNameValueArray(aggregatedData?.tgiRequestsByModel);
  }, [aggregatedData]);

  // Memoized compute distribution (backend pre-filters by provisionType)
  const computeDistributionData = useMemo(() => {
    return toNameValueArray(aggregatedData?.computeDistribution);
  }, [aggregatedData]);

  // Memoized avg tokens by model (backend pre-filters by provisionType)
  const avgTokensByModelData = useMemo(() => {
    return toNameValueArray(aggregatedData?.avgTokensByModel);
  }, [aggregatedData]);

  // Memoized requests over time (backend pre-filters by provisionType)
  const requestsOverTimeData = useMemo(() => {
    return toDateCountArray(aggregatedData?.requestsOverTime);
  }, [aggregatedData]);

  const handleTimeRangeChange = (range) => {
    const end = new Date();
    let start = new Date();

    if (range === "24h") {
      start.setDate(end.getDate() - 1);
    } else if (range === "7d") {
      start.setDate(end.getDate() - 7);
    } else if (range === "30d") {
      start.setDate(end.getDate() - 30);
    }

    setStartDate(start.toISOString().split("T")[0]);
    setEndDate(end.toISOString().split("T")[0]);
    setSelectedTimeRange(range);
  };

  // Helper to build aggregated endpoint URL using query parameters
  const buildAggregatedUrl = (s, e, m, p) => {
    const params = new URLSearchParams();
    if (s && s.trim()) params.append("startDate", s.trim());
    if (e && e.trim()) params.append("endDate", e.trim());
    if (m && m.trim() && m !== "all") params.append("modelName", m.trim());
    if (p && p.trim() && p !== "all") params.append("provisionType", p.trim());
    const queryString = params.toString();
    return `${api_url}/get-aggregated-metrics${
      queryString ? "?" + queryString : ""
    }`;
  };

  // Fetch aggregated data from backend (primary fetch powering the UI)
  useEffect(() => {
    const isTimeoutError = (error) =>
      error?.code === "ECONNABORTED" ||
      error?.message?.toLowerCase().includes("timeout");

    const fetchAggregated = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = buildAggregatedUrl(
          startDate,
          endDate,
          selectedModel,
          provisionType,
        );
        console.log("Fetching aggregated metrics from:", url);
        let res = await axiosInstance.get(url);
        console.log("Aggregated metrics response:", res.data);
        setAggregatedData(res.data || {});
        setLastRefreshed(new Date());
      } catch (err) {
        if (isTimeoutError(err)) {
          console.error("Aggregated metrics request timed out:", err);
          setError(
            "Request timed out while loading metrics. Please narrow the date range or try again.",
          );
          setAggregatedData(null);
          return;
        }
        try {
          // Fallback to query params style if backend uses @RequestParam
          console.log(
            "Path-style request failed, trying query params fallback",
          );
          const res = await axiosInstance.get(
            `${api_url}/get-aggregated-metrics`,
            {
              params: {
                startDate: startDate || "all",
                endDate: endDate || "all",
                modelName: selectedModel || "all",
                provisionType: provisionType || "all",
              },
            },
          );
          console.log("Fallback response:", res.data);
          setAggregatedData(res.data || {});
          setRefreshKey(0); // Reset refresh key after successful fetch
        } catch (innerErr) {
          console.error("Error fetching aggregated metrics:", innerErr || err);
          if (isTimeoutError(innerErr)) {
            setError(
              "Request timed out while loading metrics. Please narrow the date range or try again.",
            );
          } else {
            setError("Failed to load aggregated metrics.");
          }
          setAggregatedData(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAggregated();
  }, [
    startDate,
    endDate,
    selectedModel,
    provisionType,
    refreshKey,
    selectedTimeRange,
  ]);

  // Debug log aggregatedData whenever it changes
  useEffect(() => {
    console.log("aggregatedData updated:", aggregatedData);
  }, [aggregatedData]);

  // Update available models when aggregated data changes
  useEffect(() => {
    if (aggregatedData?.availableModels) {
      const models = Array.isArray(aggregatedData.availableModels)
        ? aggregatedData.availableModels
        : Object.keys(aggregatedData.availableModels);
      setAvailableModels(models.sort());
    }

    if (aggregatedData?.availableProvisionTypes) {
      const types = Array.isArray(aggregatedData.availableProvisionTypes)
        ? aggregatedData.availableProvisionTypes
        : Object.keys(aggregatedData.availableProvisionTypes);
      setAvailableProvisionTypes(types.sort());
    }
  }, [aggregatedData]);

  // Total requests derived from aggregated payload (backend pre-filters by provisionType)
  const totalRequests = useMemo(() => {
    const data = toDateCountArray(aggregatedData?.requestsOverTime);
    return data.reduce((sum, d) => sum + (d?.count || 0), 0) || 0;
  }, [aggregatedData]);

  // Avg response time derived from model performance (backend pre-filters by provisionType)
  const tgiTotals = useMemo(() => {
    const perf = aggregatedData?.modelPerformance;

    let weightedLatency = 0;
    let totalCount = 0;

    if (perf && typeof perf === "object") {
      Object.values(perf).forEach((d) => {
        const count = d?.count ?? 0;
        const latencySeconds = d?.avgTotalTime ?? d?.avgInferenceTime ?? 0;
        weightedLatency += latencySeconds * count;
        totalCount += count;
      });
    }

    // Convert from seconds to milliseconds
    const avgLatency =
      totalCount > 0 ? Math.round((weightedLatency / totalCount) * 1000) : 0;

    return { totalTokens: 0, avgLatency };
  }, [aggregatedData]);

  // Total tokens (backend pre-filters by provisionType)
  const totalTokens = useMemo(() => {
    return aggregatedData?.totalTokens ?? 0;
  }, [aggregatedData]);

  // Unique users count (backend pre-filters by provisionType)
  const uniqueUsers = useMemo(() => {
    return aggregatedData?.topUsers ?? 0;
  }, [aggregatedData]);

  // Get stats for a selected model (when not "all")
  const selectedModelStats = useMemo(() => {
    if (selectedModel === "all" || !aggregatedData) return null;

    // Get model performance data - handle both array and object formats
    const modelPerfRaw =
      aggregatedData?.modelPerformance ??
      aggregatedData?.modelPerformanceMap ??
      aggregatedData?.modelPerformanceStats;

    let perf = null;
    if (Array.isArray(modelPerfRaw)) {
      perf = modelPerfRaw.find(
        (d) => d?.model === selectedModel || d?.name === selectedModel,
      );
    } else if (modelPerfRaw && typeof modelPerfRaw === "object") {
      perf = modelPerfRaw[selectedModel];
    }

    // Latency: avgTotalTime is in seconds, convert to ms
    const avgResponseTime =
      perf?.avgTotalTime != null
        ? Math.round(perf.avgTotalTime * 1000)
        : perf?.avgInferenceTime != null
          ? Math.round(perf.avgInferenceTime * 1000)
          : 0;

    // Tokens: prefer perf.avgTokens, fallback to avgTokensByModel
    let avgTokens = perf?.avgTokens ?? 0;
    if (avgTokens === 0) {
      const avgTokensData = aggregatedData?.avgTokensByModel;
      if (avgTokensData) {
        if (Array.isArray(avgTokensData)) {
          const found = avgTokensData.find((d) => d?.name === selectedModel);
          avgTokens = found?.value ?? 0;
        } else if (typeof avgTokensData === "object") {
          avgTokens = avgTokensData[selectedModel] ?? 0;
        }
      }
    }

    // Request count: prefer perf.count, fallback to tgiRequestsByModel
    let modelRequests = perf?.count ?? 0;
    if (modelRequests === 0) {
      const tgiRequests = aggregatedData?.tgiRequestsByModel;
      if (tgiRequests) {
        if (Array.isArray(tgiRequests)) {
          const found = tgiRequests.find((d) => d?.name === selectedModel);
          modelRequests = found?.value ?? 0;
        } else if (typeof tgiRequests === "object") {
          modelRequests = tgiRequests[selectedModel] ?? 0;
        }
      }
    }

    // Compute type: prefer perf.computeType, fallback to computeDistribution
    const computeType =
      perf?.computeType ??
      aggregatedData?.computeDistribution?.[selectedModel] ??
      "N/A";

    return {
      name: selectedModel,
      requests: modelRequests,
      avgResponseTime,
      avgTokens: Math.round(avgTokens),
      computeType: typeof computeType === "number" ? "N/A" : computeType,
    };
  }, [selectedModel, aggregatedData]);

  // Determine which user tabs have data
  const availableUserTabs = useMemo(() => {
    const tabs = [];
    if (toUserArray(aggregatedData?.topClientIdUsers).length > 0)
      tabs.push("client-id");
    if (
      provisionType === "all" &&
      toUserArray(aggregatedData?.topGmidUsers).length > 0
    )
      tabs.push("gmid");
    if (toUserArray(aggregatedData?.topAsmsUsers).length > 0) tabs.push("asms");
    return tabs;
  }, [aggregatedData, provisionType]);

  // Truncate and tooltip for long user/client IDs
  const TRUNCATE_LENGTH = 10;
  const TruncatedYAxisTick = ({ x, y, payload }) => {
    const value = payload.value || "";
    const truncated =
      value.length > TRUNCATE_LENGTH
        ? value.slice(0, TRUNCATE_LENGTH) + "..."
        : value;
    return (
      <OverlayTrigger
        placement="right"
        overlay={<BSTooltip id={`tooltip-${value}`}>{value}</BSTooltip>}
      >
        <foreignObject
          x={x - 90}
          y={y - 10}
          width={90}
          height={20}
          style={{ pointerEvents: "none" }}
        >
          <div
            onClick={() => copyText(value)}
            title="Click to copy"
            style={{
              width: "90px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "right",
              fontSize: "12px",
              color: "#666",
              pointerEvents: "auto",
              cursor: "pointer",
            }}
          >
            {truncated}
          </div>
        </foreignObject>
      </OverlayTrigger>
    );
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Container
      fluid
      className="p-3"
      style={{ height: "100%", overflowY: "auto" }}
    >
      <Row className="mb-2">
        <Col>
          {/* Last Refreshed timestamp and Refresh button */}
          <div className="d-flex justify-content-start align-items-center gap-3">
            {lastRefreshed && (
              <span className="text-muted small">
                Last refreshed: {lastRefreshed.toLocaleString()}
              </span>
            )}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setRefreshKey((k) => k + 1)}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh Data"}
            </Button>
          </div>
        </Col>
      </Row>
      {/* Overview */}
      <Card className="mb-4">
        <Card.Body>
          <Form>
            <Row className="filter-row align-items-end">
              <Col md={2}>
                <Form.Group controlId="timeRange">
                  <Form.Label>Time Range</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <BSTooltip id="time-range-tooltip">
                        Select the time range for data aggregation
                      </BSTooltip>
                    }
                  >
                    <span className="d-block">
                      <Form.Select
                        value={selectedTimeRange}
                        onChange={(e) => handleTimeRangeChange(e.target.value)}
                        disabled={loading}
                      >
                        {/* {selectedTimeRange === "custom" && (
                          <option value="custom" hidden>
                            Custom
                          </option>
                        )} */}
                        <option value="24h">Last 24 Hours</option>
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                      </Form.Select>
                    </span>
                  </OverlayTrigger>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId="startDate">
                  <Form.Label>Start Date</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <BSTooltip id="start-date-tooltip">
                        Date range is controlled by Time Range
                      </BSTooltip>
                    }
                  >
                    <span className="d-block">
                      <Form.Control
                        type="text"
                        value={startDate}
                        readOnly
                        aria-readonly="true"
                        tabIndex={-1}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{ cursor: "default", caretColor: "transparent" }}
                        disabled={loading}
                        // onChange={(e) => {
                        //   // Only update if a date was actually selected (value is not empty)
                        //   if (e.target.value.trim()) {
                        //     setStartDate(e.target.value);
                        //     setSelectedTimeRange("custom");
                        //   }
                        // }}
                      />
                    </span>
                  </OverlayTrigger>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId="endDate">
                  <Form.Label>End Date</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <BSTooltip id="end-date-tooltip">
                        Date range is controlled by Time Range
                      </BSTooltip>
                    }
                  >
                    <span className="d-block">
                      <Form.Control
                        type="text"
                        value={endDate}
                        readOnly
                        aria-readonly="true"
                        tabIndex={-1}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{ cursor: "default", caretColor: "transparent" }}
                        disabled={loading}
                        // onChange={(e) => {
                        //   // Only update if a date was actually selected (value is not empty)
                        //   if (e.target.value.trim()) {
                        //     setEndDate(e.target.value);
                        //     setSelectedTimeRange("custom");
                        //   }
                        // }}
                      />
                    </span>
                  </OverlayTrigger>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="modelFilter">
                  <Form.Label>Model</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <BSTooltip id="model-tooltip">
                        Filter metrics by models
                      </BSTooltip>
                    }
                  >
                    <span className="d-block">
                      <Form.Select
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        disabled={loading}
                      >
                        <option value="all">All Models</option>
                        {availableModels.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </Form.Select>
                    </span>
                  </OverlayTrigger>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="provisionType">
                  <Form.Label>Provision Type</Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <BSTooltip id="provision-type-tooltip">
                        Filter metrics by provision type
                      </BSTooltip>
                    }
                  >
                    <span className="d-block">
                      <Form.Select
                        value={provisionType}
                        onChange={(e) => setProvisionType(e.target.value)}
                        disabled={loading}
                      >
                        <option value="all">All</option>
                        {availableProvisionTypes.map((type) => (
                          <option key={type} value={type}>
                            {formatProvisionTypeLabel(type)}
                          </option>
                        ))}
                      </Form.Select>
                    </span>
                  </OverlayTrigger>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          <div className="mt-2">Loading visualization data...</div>
        </div>
      )}

      {!loading && error && <Alert variant="danger">{error}</Alert>}

      {/* Only show the "No requests found" alert if no data */}
      {!loading && !error && totalRequests === 0 && (
        <Alert variant="info" className="mb-4">
          No requests found for the selected filters
          {provisionType !== "all" && (
            <span> ({formatProvisionTypeLabel(provisionType)})</span>
          )}
          {selectedModel !== "all" && <span> and model "{selectedModel}"</span>}{" "}
          within the selected time range. Try adjusting your filters.
        </Alert>
      )}

      {/* Only show the visualization if there is data */}
      {!loading && !error && totalRequests > 0 && (
        <>
          <Row className="mb-4">
            <Col xs={12}>
              <Card className="shadow-sm">
                <Card.Body className="p-0">
                  <Row className="text-center g-0">
                    <Col
                      md={
                        selectedModel !== "all" && isTeiModel(selectedModel)
                          ? 6
                          : 3
                      }
                      className="p-3"
                    >
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <BSTooltip id="total-requests-tooltip">
                            Total number of API requests
                          </BSTooltip>
                        }
                      >
                        <div>
                          <div className="text-muted text-uppercase small fw-bold mb-1">
                            Total Requests
                          </div>
                          <div className="h2 mb-0 text-primary">
                            {(totalRequests || 0).toLocaleString()}
                          </div>
                        </div>
                      </OverlayTrigger>
                    </Col>
                    <Col
                      md={
                        selectedModel !== "all" && isTeiModel(selectedModel)
                          ? 6
                          : 3
                      }
                      className="p-3 border-start"
                    >
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <BSTooltip id="unique-users-tooltip">
                            Number of distinct users making requests
                          </BSTooltip>
                        }
                      >
                        <div>
                          <div className="text-muted text-uppercase small fw-bold mb-1">
                            Unique Users
                          </div>
                          <div className="h2 mb-0 text-success">
                            {(uniqueUsers || 0).toLocaleString()}
                          </div>
                        </div>
                      </OverlayTrigger>
                    </Col>
                    {!(
                      selectedModel !== "all" && isTeiModel(selectedModel)
                    ) && (
                      <Col md={3} className="p-3 border-start">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <BSTooltip id="avg-response-time-tooltip">
                              Weighted average response time across all models
                            </BSTooltip>
                          }
                        >
                          <div>
                            <div className="text-muted text-uppercase small fw-bold mb-1">
                              Avg Response Time (Weighted)
                            </div>
                            <div className="h2 mb-0 text-warning">
                              {tgiTotals?.avgLatency || 0} ms
                            </div>
                          </div>
                        </OverlayTrigger>
                      </Col>
                    )}
                    {!(
                      selectedModel !== "all" && isTeiModel(selectedModel)
                    ) && (
                      <Col md={3} className="p-3 border-start">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <BSTooltip id="total-tokens-tooltip">
                              Total tokens generated by all models
                            </BSTooltip>
                          }
                        >
                          <div>
                            <div className="text-muted text-uppercase small fw-bold mb-1">
                              Total Tokens
                            </div>
                            <div className="h2 mb-0 text-info">
                              {(totalTokens || 0).toLocaleString()}
                            </div>
                          </div>
                        </OverlayTrigger>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col xs={12}>
              <Card>
                <Card.Header>Requests Over Time</Card.Header>
                <Card.Body>
                  {/* Load depending on model filter - All/specific model */}
                  {selectedModel === "all" ? (
                    <div style={{ width: "100%", height: 300 }}>
                      <SafeResponsiveContainer>
                        <LineChart data={requestsOverTimeData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="count"
                            stroke="#0d6efd"
                            name="Request Volume"
                          />
                        </LineChart>
                      </SafeResponsiveContainer>
                    </div>
                  ) : (
                    /* Show for specific model */
                    <div style={{ width: "100%", height: 300 }}>
                      <SafeResponsiveContainer>
                        <LineChart
                          data={toDateCountArray(
                            aggregatedData?.modelRequestsOverTime,
                          )}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="count"
                            stroke="#0d6efd"
                            name="Request Volume"
                          />
                        </LineChart>
                      </SafeResponsiveContainer>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            {!(selectedModel !== "all" && isTeiModel(selectedModel)) && (
              <Col md={6}>
                <Card className="h-100">
                  <Card.Header>
                    {selectedModel === "all"
                      ? "Model Usage Distribution"
                      : `Model Details: ${selectedModel}`}
                  </Card.Header>
                  <Card.Body>
                    {selectedModel === "all" ? (
                      <div style={{ width: "100%", height: 300 }}>
                        <SafeResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={tgiRequestsByModelData}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              label={({ name, percent }) =>
                                `${name} ${(percent * 100).toFixed(1)}%`
                              }
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              minAngle={5}
                              paddingAngle={2}
                            >
                              {tgiRequestsByModelData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </SafeResponsiveContainer>
                      </div>
                    ) : selectedModelStats ? (
                      <div
                        style={{ height: 300 }}
                        className="d-flex flex-column justify-content-center"
                      >
                        <Row className="text-center g-3">
                          <Col xs={6}>
                            <div className="border rounded p-3 h-100">
                              <div className="text-muted text-uppercase small fw-bold mb-2">
                                Total Requests
                              </div>
                              <div className="h3 mb-0 text-primary">
                                {selectedModelStats.requests.toLocaleString()}
                              </div>
                            </div>
                          </Col>
                          <Col xs={6}>
                            <div className="border rounded p-3 h-100">
                              <div className="text-muted text-uppercase small fw-bold mb-2">
                                Avg Response Time
                              </div>
                              <div className="h3 mb-0 text-warning">
                                {selectedModelStats.avgResponseTime.toLocaleString()}{" "}
                                ms
                              </div>
                            </div>
                          </Col>
                          <Col xs={6}>
                            <div className="border rounded p-3 h-100">
                              <div className="text-muted text-uppercase small fw-bold mb-2">
                                Avg Tokens Generated
                              </div>
                              <div className="h3 mb-0 text-success">
                                {selectedModelStats.avgTokens.toLocaleString()}
                              </div>
                            </div>
                          </Col>
                          <Col xs={6}>
                            <div className="border rounded p-3 h-100">
                              <div className="text-muted text-uppercase small fw-bold mb-2">
                                Compute Type
                              </div>
                              <div className="h3 mb-0 text-info">
                                {selectedModelStats.computeType}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center text-muted"
                        style={{ height: 300 }}
                      >
                        No data available for this model
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            )}
            <Col
              md={selectedModel !== "all" && isTeiModel(selectedModel) ? 12 : 6}
            >
              <Card className="h-100">
                <Card.Header>Top 10 Unique Users by Request Volume</Card.Header>
                <Card.Body>
                  {provisionType === "all" || provisionType === "Galileo" ? (
                    <Tabs
                      activeKey={
                        availableUserTabs.includes(activeUserTab)
                          ? activeUserTab
                          : availableUserTabs[0]
                      }
                      onSelect={(k) => setActiveUserTab(k)}
                      className="mb-3"
                    >
                      {availableUserTabs.includes("client-id") && (
                        <Tab eventKey="client-id" title="Client ID">
                          <div style={{ width: "100%", height: 300 }}>
                            <SafeResponsiveContainer>
                              <BarChart
                                data={toUserArray(
                                  aggregatedData?.topClientIdUsers,
                                )}
                                layout="vertical"
                                margin={{
                                  top: 8,
                                  right: 16,
                                  bottom: 8,
                                  left: 16,
                                }}
                                barCategoryGap="20%"
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis
                                  dataKey="user"
                                  type="category"
                                  width={90}
                                  tickLine={false}
                                  axisLine={false}
                                  interval={0}
                                  tick={<TruncatedYAxisTick />}
                                />
                                <Tooltip />
                                <Legend />
                                <Bar
                                  dataKey="count"
                                  fill="#82ca9d"
                                  name="Requests"
                                />
                              </BarChart>
                            </SafeResponsiveContainer>
                          </div>
                        </Tab>
                      )}
                      {availableUserTabs.includes("gmid") && (
                        <Tab eventKey="gmid" title="GMID">
                          <div style={{ width: "100%", height: 300 }}>
                            <SafeResponsiveContainer>
                              <BarChart
                                data={toUserArray(aggregatedData?.topGmidUsers)}
                                layout="vertical"
                                margin={{
                                  top: 8,
                                  right: 16,
                                  bottom: 8,
                                  left: 16,
                                }}
                                barCategoryGap="20%"
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis
                                  dataKey="user"
                                  type="category"
                                  width={90}
                                  tickLine={false}
                                  axisLine={false}
                                  interval={0}
                                  tick={<TruncatedYAxisTick />}
                                />
                                <Tooltip />
                                <Legend />
                                <Bar
                                  dataKey="count"
                                  fill="#82ca9d"
                                  name="Requests"
                                />
                              </BarChart>
                            </SafeResponsiveContainer>
                          </div>
                        </Tab>
                      )}
                      {availableUserTabs.includes("asms") && (
                        <Tab eventKey="asms" title="ASMS">
                          <div style={{ width: "100%", height: 300 }}>
                            <SafeResponsiveContainer>
                              <BarChart
                                data={toUserArray(
                                  aggregatedData?.topAsmsUsers,
                                ).sort((a, b) => b.count - a.count)}
                                layout="vertical"
                                margin={{
                                  top: 8,
                                  right: 16,
                                  bottom: 8,
                                  left: 16,
                                }}
                                barCategoryGap="20%"
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis
                                  dataKey="user"
                                  type="category"
                                  width={90}
                                  tickLine={false}
                                  axisLine={false}
                                  interval={0}
                                  tick={<TruncatedYAxisTick />}
                                />
                                <Tooltip />
                                <Legend />
                                <Bar
                                  dataKey="count"
                                  fill="#82ca9d"
                                  name="Requests"
                                />
                              </BarChart>
                            </SafeResponsiveContainer>
                          </div>
                        </Tab>
                      )}
                    </Tabs>
                  ) : (
                    /* Self Provisioned (GMID) - show single chart */
                    <Tabs
                      activeKey="gmid"
                      onSelect={() => {}}
                      className="mb-3"
                      id="top-users-tabs"
                      style={{ pointerEvents: "none", cursor: "default" }}
                    >
                      <Tab eventKey="gmid" title="GMID">
                        <div style={{ width: "100%", height: 300 }}>
                          <SafeResponsiveContainer>
                            <BarChart
                              data={toUserArray(aggregatedData?.topGmidUsers)}
                              layout="vertical"
                              margin={{
                                top: 8,
                                right: 16,
                                bottom: 8,
                                left: 16,
                              }}
                              barCategoryGap="20%"
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" />
                              <YAxis
                                dataKey="user"
                                type="category"
                                width={calculateYAxisWidth(
                                  toUserArray(aggregatedData?.topGmidUsers),
                                )}
                                tickLine={false}
                                axisLine={false}
                                interval={0}
                                tick={<TruncatedYAxisTick />}
                              />
                              <Tooltip />
                              <Legend />
                              <Bar
                                dataKey="count"
                                fill="#82ca9d"
                                name="Requests"
                              />
                            </BarChart>
                          </SafeResponsiveContainer>
                        </div>
                      </Tab>
                    </Tabs>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {selectedModel === "all" && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header>Avg Response Time by Model</Card.Header>
                    <Card.Body>
                      <div style={{ width: "100%", height: 300 }}>
                        <SafeResponsiveContainer>
                          <BarChart data={modelPerfData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="model" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar
                              dataKey="avgResponseTime"
                              fill="#ff8042"
                              name="Avg Time (ms)"
                            />
                          </BarChart>
                        </SafeResponsiveContainer>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header>Compute Resource Distribution</Card.Header>
                    <Card.Body>
                      <div style={{ width: "100%", height: 300 }}>
                        <SafeResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={computeDistributionData}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              label={({ name, percent }) =>
                                `${name} ${(percent * 100).toFixed(0)}%`
                              }
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              minAngle={5}
                              paddingAngle={2}
                            >
                              {computeDistributionData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </SafeResponsiveContainer>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12}>
                  <Card>
                    <Card.Header>Avg Tokens Generated by Model</Card.Header>
                    <Card.Body>
                      <div style={{ width: "100%", height: 300 }}>
                        <SafeResponsiveContainer>
                          <BarChart data={avgTokensByModelData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar
                              dataKey="value"
                              fill="#00C49F"
                              name="Avg Tokens"
                            />
                          </BarChart>
                        </SafeResponsiveContainer>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default MetricsVisual;
